import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Table, message } from '@maxtropy/components';
import { apiV2AreaCategoryListPost, apiV2AreaCategoryUpdatePost } from '@maxtropy/device-customer-apis-v2';
export interface ClassificationItem {
  id?: number;
  index?: number;
  name?: string;
  description?: string;
}

interface AreaClassificationSettingProps {
  visible: boolean;
  onCancel: () => void;
}

const MAX_NAME_LENGTH = 10;

const AreaClassificationSetting: React.FC<AreaClassificationSettingProps> = ({ visible, onCancel }) => {
  const [form] = Form.useForm();
  const [classificationData, setClassificationData] = useState<ClassificationItem[]>([]);

  useEffect(() => {
    apiV2AreaCategoryListPost().then(res => {
      const dataList = res.list || [];

      // 如果后端没有返回数据，显示默认分类
      if (!res.list || res.list.length === 0) {
        const result: ClassificationItem[] = [];
        for (let i = 1; i <= 10; i++) {
          result.push({
            index: i,
            name: `分类${i}`,
            description: `分类${i}`,
          });
        }
        setClassificationData(result);
        return;
      }

      const dataMap = new Map<number, ClassificationItem>();
      dataList.forEach(item => {
        if (item.index) {
          dataMap.set(item.index, item);
        }
      });

      // 生成 1 到 10 的序列
      const result: ClassificationItem[] = [];

      for (let i = 1; i <= 10; i++) {
        const backendItem = dataMap.get(i);

        if (backendItem) {
          // 后端有这条数据 -> 使用后端返回的 description
          result.push({
            index: i,
            name: `分类${i}`,
            description: backendItem.description,
          });
        } else {
          // 后端缺失这条数据 -> 显示空字符串
          result.push({
            index: i,
            name: `分类${i}`,
            description: '',
          });
        }
      }
      setClassificationData(result);
    });
  }, []);

  // 验证自定义名称是否重复
  const validateUniqueNames = (data: ClassificationItem[]): string | null => {
    const nameSet = new Set<string>();
    for (const item of data) {
      const trimmedName = item.description?.trim();
      if (!trimmedName) {
        continue; // 空值允许，非必填
      }
      if (nameSet.has(trimmedName)) {
        return `自定义分类名称 "${trimmedName}" 已存在，请勿重复输入`;
      }
      nameSet.add(trimmedName);
    }
    return null;
  };

  // 处理保存
  const handleSave = (values: any) => {
    // 构建完整的分类数据，过滤掉 description 为空或空格的项
    const updatedData: ClassificationItem[] = classificationData
      .map((item, idx) => ({
        ...item,
        description: values.list?.[idx]?.description,
      }))
      .filter(item => item.description && item.description.trim() !== '');

    // 验证重复
    const duplicateError = validateUniqueNames(updatedData);
    if (duplicateError) {
      message.error(duplicateError);
      return;
    }

    // 调用接口保存
    const requestData = {
      list: updatedData.map(item => ({
        index: item.index,
        description: item.description,
      })),
    };

    apiV2AreaCategoryUpdatePost(requestData)
      .then(() => {
        message.success('保存成功');
        onCancel();
      })
      .catch(error => {
        console.error('保存失败:', error);
      });
  };

  // 处理取消
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // 自定义输入框组件
  const CustomInput = ({ record, index }: { record: ClassificationItem; index: number }) => {
    return (
      <Form.Item
        name={['list', index, 'description']}
        rules={[
          {
            max: MAX_NAME_LENGTH,
            message: `自定义分类名称最多${MAX_NAME_LENGTH}个字符`,
          },
        ]}
        style={{ marginBottom: 0 }}
        initialValue={record.description}
      >
        <Input placeholder="请输入自定义分类名称" />
      </Form.Item>
    );
  };

  const columns = [
    {
      title: '分类',
      dataIndex: 'name',
      width: '50%',
    },
    {
      title: '自定义分类名称',
      dataIndex: 'description',
      width: '50%',
      render: (_: unknown, record: ClassificationItem, index: number) => <CustomInput record={record} index={index} />,
    },
  ];

  return (
    <Modal
      title="分类设置"
      open={visible}
      onCancel={handleCancel}
      okText="保存"
      onOk={form.submit}
      width={600}
      centered
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <Table columns={columns} dataSource={classificationData} rowKey="index" pagination={false} bordered />
        </div>
      </Form>
    </Modal>
  );
};

export default AreaClassificationSetting;
